'use client';

import { useTranslations } from 'next-intl';
import { getPriceValueTranslation } from '@/entities/cart';
import type { OrderReceiptLine } from '@/entities/order/lib/order-receipt';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';
import { Icon } from '@/shared/ui/icon';
import './order-modal/order-modal.scss';

export type { OrderReceiptLine as AddonLine } from '@/entities/order/lib/order-receipt';

type AddonLine = OrderReceiptLine;

type StampedAddonLine = AddonLine & { index: number; children: StampedAddonLine[] };

const stampAddonLines = (nodes: AddonLine[], ids: { n: number }): StampedAddonLine[] =>
    nodes.map((node) => {
        const index = ids.n++;
        return { ...node, index, children: stampAddonLines(node.children, ids) };
    });

/** Nested addon lines with the same branch used by order history. */
export const OrderAddonTree = ({
    lines,
    currency,
    nested = false,
}: {
    lines: AddonLine[];
    currency: Currency;
    nested?: boolean;
}) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const stamped = nested ? (lines as StampedAddonLine[]) : stampAddonLines(lines, { n: 0 });
    const textOf = (line: StampedAddonLine) => {
        const name = getLocalizedValue(line.name);
        const variant = line.variantName ? getLocalizedValue(line.variantName) : '';
        const label = variant ? `${name} - ${variant}` : name;
        const sign = line.count < 0 ? '-' : '+';
        const showQty = Math.abs(line.count) > 1;
        const qty = showQty ? `${Math.abs(line.count)}x ` : ' ';
        return `${sign}${qty}${label}`;
    };
    const priceOf = (amount: number) =>
        getPriceValueTranslation({
            amount,
            currency: currency,
            t,
        });
    const nodes = stamped.map((line) => (
        <div key={line.index} className="order-modal__mod-node">
            <p>
                {line.package ? (
                    <>
                        <Icon name="package" size={14} className="order-modal__mod-package-icon" />
                        <span className="order-modal__mod-package">{getLocalizedValue(line.name)}</span>
                    </>
                ) : (
                    <>
                        <span
                            className={line.count < 0 ? 'order-modal__mod-removed' : 'order-modal__mod-added'}
                            data-testid={`order-modal-item-addon-${line.index}`}
                        >
                            {textOf(line)}
                        </span>
                        {line.count < 0 && (
                            <span className="order-modal__mod-removed-note">{t('WORDS.REMOVED_TAG')}</span>
                        )}
                    </>
                )}
                {line.price > 0 && (
                    <span
                        className={
                            line.package
                                ? 'order-modal__mod-price order-modal__mod-price--package'
                                : 'order-modal__mod-price'
                        }
                    >
                        {`+${priceOf(line.price)}`}
                    </span>
                )}
            </p>
            {line.children.length > 0 && <OrderAddonTree lines={line.children} currency={currency} nested />}
        </div>
    ));
    if (nested) return <div className="order-modal__mod-branch">{nodes}</div>;
    return <>{nodes}</>;
};
